import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const PERSONAS = {
  individual: {
    code: "01",
    title: "Individuals",
    tag: "Students · Early-career professionals · Career switchers",
    hero: "Turn your background into a launch-ready role in India's space economy.",
    intro: "Whether you're a fourth-year engineering student, a BFSI analyst curious about satellite insurance, a lawyer eyeing space policy, or a designer drawn to mission control — Forte² Space maps your existing strengths onto specific roles in the Indian space economy and shows you the shortest path to get there.",
    dimensions: ["Technical", "Commercial", "Regulatory", "Funding Literacy", "Insurance Literacy", "Sustainability"],
    benefits: [
      { t: "Career Forte Report", d: "AI-authored report mapping your background to specific space-sector roles with rationale, expected salary bands and progression paths." },
      { t: "Personalised Learning Path", d: "A prioritised sequence of modules — orbital mechanics, space policy, remote sensing, insurance & finance — built around the gaps in your Forte² Score." },
      { t: "Mentor Connect", d: "Access ex-ISRO systems architects, EO data scientists, space policy counsels and satellite-insurance leads via a curated mentor network." },
      { t: "Verified Forte² Badge", d: "A shareable credential recruiters can trust — signals not just skills but readiness across technical, regulatory and commercial dimensions." },
    ],
    example: "Example — A 3-year BFSI analyst scored 78/100 on Insurance Literacy and 62/100 on Commercial. Report recommended: Satellite Insurance Analyst (18–24 LPA) at Orbix Risk. Learning path: Space Insurance 101 + Orbital Mechanics for Non-Engineers. 12 weeks.",
  },
  startup: {
    code: "02",
    title: "Startups",
    tag: "0–50 employees · Pre-seed → Series B",
    hero: "Score your startup like an investor would — before they do.",
    intro: "Space startups don't fail on technology — they fail because commercial traction is thin, IN-SPACe licensing is delayed, insurance strategy is undefined, or the data-room isn't investor-ready. Forte² Space scores you across all of these, then hands you a prioritised action list to close each gap.",
    dimensions: ["Technology Readiness", "Commercial Traction", "Regulatory Readiness", "Funding Readiness", "Insurance Readiness", "Team &amp; Governance"],
    benefits: [
      { t: "Startup Forte Diagnostic", d: "Six-dimension readiness score with an AI-authored verdict (e.g. 'insurance-ready but funding narrative weak') and a ranked action list." },
      { t: "Investor-ready Pitch Signal", d: "Get an objective third-party score investors can reference — cuts due-diligence friction and increases fund-raise credibility." },
      { t: "Regulatory Checklist", d: "IN-SPACe / DoT / WPC-aware licensing checklist mapped to your mission profile, powered by Orbix's regulatory intelligence." },
      { t: "Talent Marketplace Access", d: "Post space-sector roles at zero marginal cost during your cohort and access pre-scored candidates by dimension." },
    ],
    example: "Example — A pre-seed EO startup scored Tech 62, Funding 25, Insurance 12. AI verdict: 'Strong prototype, weak fundability, insurance blind-spot.' Action list: build 24-month runway model (4 wks), scope launch cover with Orbix (6 wks).",
  },
  corporate: {
    code: "03",
    title: "Corporates",
    tag: "Telecom · BFSI · Manufacturing · Defense",
    hero: "Map your existing capabilities onto India's $44B space opportunity.",
    intro: "Your telecom division already has RF expertise. Your insurance arm underwrites complex liability. Your IT services team runs GIS pipelines. The question isn't whether you belong in the space economy — it's which sub-sector to enter first and how to redeploy talent to get there.",
    dimensions: ["Capability Fit", "Market Access", "Regulatory Awareness", "Workforce Readiness", "Partnerships", "Investment Appetite"],
    benefits: [
      { t: "Corporate Forte Map", d: "Executive-grade diagnostic mapping your existing capabilities to specific space sub-sectors, ranked by strategic fit and revenue potential." },
      { t: "Workforce Redeployment Plan", d: "Upskill roadmaps for existing employees — technical, regulatory and commercial modules — so you don't have to hire from scratch." },
      { t: "Partner &amp; Startup Directory", d: "A curated shortlist of space startups and IN-SPACe partners aligned to your diversification thesis." },
      { t: "Analytics Dashboard", d: "Track cohort progress, capability build-out and market signals in a single executive dashboard." },
    ],
    example: "Example — A mid-tier telecom operator mapped RF, ground infrastructure and network operations onto GEO ground stations, LEO gateways and satellite backhaul services. Redeployed 40 engineers over 6 months with targeted training pathways.",
  },
  investor: {
    code: "04",
    title: "Investors",
    tag: "VC · PE · Family offices · Corporate ventures",
    hero: "A scored, curated pipeline of India's space startups.",
    intro: "India's space sector added 250+ startups in a decade, but diligence bandwidth hasn't kept pace. Forte² Space gives you a standard, six-dimension readiness score across the pipeline — so you can filter for insurance-ready or launch-license-ready ventures in seconds, and drill into technical, commercial or team dimensions on demand.",
    dimensions: ["Sector Knowledge", "Deal Flow Access", "Diligence Capability", "Risk Underwriting", "Portfolio Fit", "Exit Understanding"],
    benefits: [
      { t: "Curated, Scored Pipeline", d: "A live pipeline of Indian space startups with Forte² Scores — filter by sub-sector, funding stage, insurance-readiness and mission phase." },
      { t: "Diligence Dashboard", d: "Dimensional drill-downs — technical readiness evidence, regulatory posture, insurance strategy — cutting diligence cycles by 30–50%." },
      { t: "Orbix Risk Signals", d: "Integrated IRI/InvRI signals from Orbix's risk intelligence — underwrite mission, launch and third-party liability with confidence." },
      { t: "Portfolio Coaching", d: "Assign Forte² Scores to portfolio companies quarterly and track improvement across dimensions — data-driven post-investment support." },
    ],
    example: "Example — Filtered for 'insurance-readiness > 60' + 'commercial-traction > 40' in Earth Observation. 6 startups surfaced. Two moved to term-sheet within 45 days.",
  },
};

export default function PersonaPreview() {
  const { slug } = useParams();
  const p = PERSONAS[slug];

  useEffect(() => {
    if (p) document.title = `Forte2 Space | For ${p.title}`;
    return () => { document.title = "Forte2 Space | Career Readiness for India's Space Economy"; };
  }, [p]);

  if (!p) return <Navigate to="/" replace />;

  return (
    <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
      <Nav />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 relative z-10">
          <div className="label-xs saffron mb-4">§ {p.code} — For {p.title}</div>
          <h1 className="font-display uppercase tracking-tighter font-black text-4xl md:text-6xl leading-[0.95]" data-testid={`persona-page-${slug}-hero`}>
            {p.hero.split(" ").map((w, i) => (
              /space|forte²|forte2|india's|india|readiness|readiness|ready|ready|score|space's|space,|space\./i.test(w)
                ? <span key={i} className="saffron">{w} </span>
                : <span key={i}>{w} </span>
            ))}
          </h1>
          <p className="mt-6 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.85)" }}>
            {p.intro}
          </p>
          <div className="mt-8 label-xs text-[color:var(--f2-muted)]">{p.tag}</div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/assessment"
              data-testid={`persona-${slug}-primary-cta`}
              className="btn-crisp btn-primary"
            >
              Take the Forte² Score →
            </Link>
            <Link
              to="/#personas"
              className="btn-crisp"
              data-testid={`persona-${slug}-back-cta`}
            >
              ← Compare Personas
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="label-xs mb-2">§ Readiness Dimensions</div>
        <h2 className="font-display uppercase font-bold tracking-tight text-3xl md:text-4xl mb-8">
          Six axes we <span className="saffron">score you on.</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {p.dimensions.map((d, i) => (
            <div key={i} className="hard-border p-6 bg-[color:var(--f2-surface)]">
              <div className="label-xs saffron">Dimension {String(i + 1).padStart(2, "0")}</div>
              <div className="font-display font-bold text-xl mt-2" dangerouslySetInnerHTML={{ __html: d }} />
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-[color:var(--f2-border)] bg-[color:var(--f2-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="label-xs mb-2">§ What You Get</div>
          <h2 className="font-display uppercase font-bold tracking-tight text-3xl md:text-4xl mb-8">
            Built for <span className="saffron">{p.title.toLowerCase()}.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {p.benefits.map((b, i) => (
              <div key={i} className="hard-border p-6 bg-black/40">
                <div className="label-xs saffron mb-2">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display font-bold text-xl mb-2" dangerouslySetInnerHTML={{ __html: b.t }} />
                <p className="text-sm text-[color:var(--f2-muted)] leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 hard-border p-6 bg-black/40">
            <div className="label-xs mb-2">Real-world snapshot</div>
            <p className="text-sm text-zinc-300 leading-relaxed">{p.example}</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-center">
        <div className="label-xs mb-4">§ Ready?</div>
        <h2 className="font-display uppercase font-bold tracking-tighter text-4xl md:text-5xl">
          Take the <span className="saffron">Forte²</span> Score.
        </h2>
        <p className="mt-4 text-[color:var(--f2-muted)] max-w-xl mx-auto">
          Five minutes. Six dimensions. One AI-authored report grounded in India&apos;s space economy.
        </p>
        <Link
          to="/assessment"
          data-testid={`persona-${slug}-footer-cta`}
          className="btn-crisp btn-primary mt-8 inline-flex"
        >
          Begin Diagnostic →
        </Link>
      </section>

      <Footer />
    </div>
  );
}
